import { computed, inject, Injectable, signal } from '@angular/core';
import { BaseApiService } from '@services/base-api.service';
import { HttpClient } from '@angular/common/http';
import { finalize, Observable, shareReplay, tap, throwError } from 'rxjs';
import { UserModel } from '@shared/models/user.model';
import { AuthService } from '@services/auth.service';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TAPiServices } from '@shared/types/TService.types';
import { md5Hash } from '@shared/utils/algorithm.utils';
import { IPasswordResetResponse } from '@modules/user/interface/response';
import { ISetPasswordRequest, IUpdateUserRequest } from '@modules/user/interface/request';
import { AuthFlowService } from '@services/auth.flow.service';
import { MessagesService } from '@services/messages.service';
import { EUpdatePasswordMode } from '@modules/auth/enum/auth.enum';

@Injectable({
    providedIn: 'root'
})
export class UserService extends BaseApiService {
    /* eslint-disable @typescript-eslint/member-ordering */
    service: TAPiServices = 'ms-user';
    msAdmin: TAPiServices = 'ms-admin';
    adminApiUrl = '';
    authService = inject(AuthService);
    protected http = inject(HttpClient);
    private readonly _user = signal<UserModel>(new UserModel());
    readonly user = computed(() => this._user());
    private readonly _signInLoading = signal<boolean>(false);
    private readonly _passwordChangePending = signal<boolean>(false);
    private readonly _profileUpdatePending = signal<boolean>(false);
    readonly signInLoading = this._signInLoading.asReadonly();
    readonly passwordChangePending = this._passwordChangePending.asReadonly();
    readonly profileUpdatePending = this._profileUpdatePending.asReadonly();
    protected messages = inject(MessagesService);
    protected router = inject(Router);
    protected authFlow = inject(AuthFlowService);

    /* eslint-enable @typescript-eslint/member-ordering */

    constructor() {
        super();
        this.apiUrl = this.getApiUrl(this.service);
        this.adminApiUrl = this.getApiUrl(this.msAdmin);
    }

    login(email: string, password: string) {
        this._signInLoading.set(true);
        const data = { email, password: md5Hash(password) };
        this.http
            .post<UserModel>(`${this.adminApiUrl}/customer/login`, data, this.options(null))
            .pipe(
                tap((response) => this.handleLogin(response)),
                finalize(() => this._signInLoading.set(false)),
                catchError((error) => {
                    this.messages.showError(['ERROR_MESSAGES', error.error.toUpperCase()].join('.'));
                    return throwError(() => error);
                })
            )
            .subscribe();
    }

    logout() {
        return this.http.delete<boolean>(`${this.adminApiUrl}/customer/logout`, this.options(this.authService.sessionToken())).pipe(finalize(() => this.authService.removeSession()));
    }

    userMe(): Observable<UserModel> {
        return this.http.get<UserModel>(`${this.adminApiUrl}/customer/me`, this.options(this.authService.sessionToken())).pipe(
            shareReplay(),
            tap((user) => this.updateUser(user)),
            catchError((err) => {
                console.error(err);
                return throwError(() => err); // oder `of(null)` je nach Logik
            })
        );
    }

    userMeUpdate(request: IUpdateUserRequest) {
        this._profileUpdatePending.set(true);
        return this.http.put<UserModel>(`${this.adminApiUrl}/customer/me/update`, request, this.options(this.authService.sessionToken())).pipe(
            tap((user) => this.authService.storeSession(user?.session)),
            finalize(() => this._profileUpdatePending.set(false))
        );
    }

    requestPasswordReset(email: string) {
        const uri = [this.adminApiUrl, 'customer/me/forgot-password'].join('/');
        this._passwordChangePending.set(true);
        return this.http.post<IPasswordResetResponse>(uri, { email }, this.options(null)).pipe(finalize(() => this._passwordChangePending.set(false)));
    }

    requestPasswordChange() {
        const uri = [this.adminApiUrl, 'customer/me/reset-password'].join('/');
        return this.http.post<IPasswordResetResponse>(uri, null, this.options(this.authService.sessionToken()));
    }

    updatePassword(data: ISetPasswordRequest, mode: EUpdatePasswordMode) {
        const uri = [this.adminApiUrl, 'customer/me/update-password-with-otp'].join('/');
        const { oneTimePassword, password, token } = data;
        const request = { oneTimePassword, password, token };
        this._passwordChangePending.set(true);
        return this.http.put<UserModel>(uri, request, this.options(this.authService.sessionToken())).pipe(
            tap((response) => {
                mode === EUpdatePasswordMode.update ? this.handlePasswordUpdate(response) : this.handleLogin(response);
            }),
            finalize(() => {
                this._passwordChangePending.set(false);
                this.authFlow.clear();
            })
        );
    }

    setPassword(data: ISetPasswordRequest) {
        const uri = [this.adminApiUrl, 'customer/me/set-password-with-otp'].join('/');
        const { oneTimePassword, password, token } = data;
        const request = { oneTimePassword, password, token };
        this._passwordChangePending.set(true);
        return this.http.post<UserModel>(uri, request, this.options(null)).pipe(
            tap((response) => {
                this.handleLogin(response);
            }),
            finalize(() => {
                this._passwordChangePending.set(false);
                this.authFlow.clear();
            })
        );
    }

    protected handlePasswordUpdate(user: UserModel) {
        this.authService.storeSession(user?.session);
        this.messages.showSuccess('MODULE_AUTH.GENERAL.PASSWORD_UPDATED_SUCCESSFULLY');
        return this.router.navigate([]);
    }

    protected handleLogin(user: UserModel) {
        this.authService.storeSession(user?.session);
        this.router.navigate(['/dashboard'], { replaceUrl: true, queryParams: {} }).then(() => {
            this.messages.clear();
        });
    }

    protected updateUser(user: UserModel) {
        delete user.session;
        this._user.set(user);
    }
}
